import type { Component } from '../router';
import { LOCALES, getLocale, setLocale, t, type Locale, type MsgKey } from '../i18n/locale';

// LocaleSwitcher — a compact segmented control (EN · ES · PT) that switches the
// active locale. Switching dispatches LOCALE_EVENT, which the router listens for
// and re-renders the active route + shell, so this control is rebuilt fresh with
// the new active state on every change (no internal listener needed). Mounted on
// both surfaces (desktop top-nav + a phone slot), mirroring HealthPill.

const SHORT: Record<Locale, string> = { en: 'EN', es: 'ES', 'pt-BR': 'PT' };
const NAME_KEY: Record<Locale, MsgKey> = {
  en: 'locale.name.en',
  es: 'locale.name.es',
  'pt-BR': 'locale.name.pt',
};

export class LocaleSwitcher implements Component {
  private root: HTMLElement | null = null;

  mount(host: HTMLElement): void {
    const group = document.createElement('div');
    group.className = 'locale-switcher';
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', t('locale.switcher.label'));

    const current = getLocale();
    for (const locale of LOCALES) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'locale-switcher__opt';
      btn.textContent = SHORT[locale];
      btn.dataset.locale = locale;
      // The two-letter glyph is ambiguous to a screen reader; announce the full
      // language name and the pressed state instead.
      btn.setAttribute('aria-label', t(NAME_KEY[locale]));
      const active = locale === current;
      btn.setAttribute('aria-pressed', String(active));
      if (active) btn.setAttribute('aria-current', 'true');
      btn.addEventListener('click', () => { void setLocale(locale); });
      group.appendChild(btn);
    }

    host.appendChild(group);
    this.root = group;
  }

  unmount(): void {
    this.root?.remove();
    this.root = null;
  }
}
